import { z } from 'zod';
import { mongoObjectIdString, optionalMongoObjectId } from './object-id-helpers.js';

const INCIDENT_STATUS_VALUES = [
    'draft',
    'submitted',
    'in_approval',
    'approved',
    'rejected',
    'returned',
    'assigned',
    'in_progress',
    'on_hold',
    'resolved',
    'closed',
    'cancelled',
];

const incidentStatusEnum = z.enum(INCIDENT_STATUS_VALUES);

const LEGACY_STATUS_MAP = {
    open: 'draft',
    reported: 'draft',
    investigating: 'in_progress',
    contained: 'in_progress',
};

const normalizeLegacyIncidentPayload = (data) => {
    const out = { ...data };
    if (out.status && LEGACY_STATUS_MAP[out.status]) {
        out.status = LEGACY_STATUS_MAP[out.status];
    }
    if (out.requestedBy == null && out.reportedBy != null) {
        out.requestedBy = out.reportedBy;
    }
    return out;
};

const severityEnum = z.enum(['critical', 'high', 'medium', 'low']);
const typeEnum = z.enum(['security', 'safety', 'operational', 'compliance', 'other']);

const poolItemSchema = z.object({
    type: z.enum(['title', 'unit', 'role', 'user']).optional(),
    value: z.string().optional(),
    label: z.string().optional(),
});

export const IncidentSchema = z.object({
    id: z.string().optional(),
    title: z.string().min(1),
    code: z.string().trim().optional(),
    description: z.string().optional(),
    type: typeEnum.optional(),
    severity: severityEnum.optional(),
    impact: severityEnum.optional(),
    urgency: severityEnum.optional(),
    escalationLevel: z.number().min(0).max(5).optional(),
    slaBreached: z.boolean().optional(),
    slaBreachedAt: z.coerce.date().optional(),
    linkedProblems: z.array(mongoObjectIdString).optional(),
    linkedChangeRequests: z.array(mongoObjectIdString).optional(),
    status: incidentStatusEnum.default('draft'),
    workflowInstanceId: optionalMongoObjectId,
    workflowTemplateId: optionalMongoObjectId,
    submittedAt: z.coerce.date().optional(),
    requestedBy: optionalMongoObjectId,
    requestedByName: z.string().optional(),
    requestedByEmail: z.string().optional(),
    requestedByUnit: optionalMongoObjectId,
    assignedUserIds: z.array(mongoObjectIdString).optional(),
    assignedToPool: z.array(poolItemSchema).optional(),
    occurredAt: z.coerce.date().optional(),
    detectedAt: z.coerce.date().optional(),
    reportedAt: z.coerce.date().optional(),
    resolvedAt: z.coerce.date().optional(),
    resolutionDueAt: z.coerce.date().optional(),
    firstResponseDueAt: z.coerce.date().optional(),
    firstResponseAt: z.coerce.date().optional(),
    sla: optionalMongoObjectId,
    template: optionalMongoObjectId,
    reportedBy: optionalMongoObjectId,
    unit: optionalMongoObjectId,
    affectedAssets: z.array(mongoObjectIdString).optional(),
    rootCause: z.string().optional(),
    rootCauseCategory: z.string().optional(),
    immediateActions: z.string().optional(),
    containmentActions: z.string().optional(),
    impactDescription: z.string().optional(),
    affectedUsers: z.number().optional(),
    dataBreachInvolved: z.boolean().optional(),
    regulatoryReporting: z.boolean().optional(),
    createdAt: z.date().optional(),
    updatedAt: z.date().optional(),
    linkedActions: z.array(mongoObjectIdString).optional(),
    comments: z.array(z.object({
        id: z.string(),
        content: z.string(),
        author: z.string().optional(),
        authorId: optionalMongoObjectId,
        createdAt: z.coerce.date().optional(),
    })).optional(),
    attachments: z.array(z.object({
        id: z.string().optional(),
        name: z.string().optional(),
        url: z.string().optional(),
        category: z.enum(['evidence', 'attachment']).optional(),
        mimeType: z.string().optional(),
        size: z.number().optional(),
        uploadedBy: optionalMongoObjectId,
        uploadedAt: z.coerce.date().optional(),
    })).optional(),
    customFields: z.record(z.unknown()).optional(),
});

export const CreateIncidentSchema = IncidentSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
}).transform(normalizeLegacyIncidentPayload);

export const UpdateIncidentSchema = IncidentSchema.omit({
    id: true,
    createdAt: true,
    updatedAt: true,
})
    .partial()
    .transform(normalizeLegacyIncidentPayload);

const INCIDENT_TERMINAL_STATUS_VALUES = Object.freeze(['resolved', 'closed', 'cancelled']);
const INCIDENT_OPEN_STATUS_VALUES = Object.freeze(
    INCIDENT_STATUS_VALUES.filter((status) => !INCIDENT_TERMINAL_STATUS_VALUES.includes(status)),
);

export { INCIDENT_STATUS_VALUES, INCIDENT_OPEN_STATUS_VALUES, INCIDENT_TERMINAL_STATUS_VALUES };
